import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import StageDot from './StageDot';
import {
  buildCommercialTooltipLines,
  getCommercialStatusMeta,
  hasCommercialSignal,
  showMismatchMark,
} from '../commercialSummary';

const MUTED = '#ABABAB';

/**
 * Indicador comercial del registro (lote / UF / CAD).
 * Punto liso de etapa (`StageDot`, el mismo del panel de Leads). Sin texto:
 * el detalle va en el tooltip. Al lado: número chico de consultas sin leer (> 0) y ⚠ gris si hay mismatch.
 *
 * `hideUnseen`: omite el número de sin leer cuando esa superficie ya lo dice de otra forma.
 * @param {{ summary: object|null, kind?: 'summary'|'rollup', isCad?: boolean, hideUnseen?: boolean }} props
 * @see docs/frontend/commercial-summary.md
 */
export default function CommercialStatusDot({ summary, kind = 'summary', isCad = false, hideUnseen = false }) {
  if (!hasCommercialSignal(summary)) return null;
  const meta = getCommercialStatusMeta(summary.status);
  const lines = buildCommercialTooltipLines(summary, { kind });
  const unseen = hideUnseen ? 0 : (summary.unseen_inquiries_count || 0);
  const mismatch = showMismatchMark(summary, { kind, isCad });
  return (
    <Tooltip
      arrow
      placement="top"
      title={(
        <Box>
          {lines.map((l) => <div key={l}>{l}</div>)}
        </Box>
      )}
    >
      <Box
        data-testid="commercial-status"
        data-status={summary.status}
        aria-label={lines.join('. ')}
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', flexShrink: 0, cursor: 'default' }}
      >
        {meta.color ? (
          <StageDot color={meta.color} data-testid="commercial-status-dot" />
        ) : null}
        {unseen > 0 && (
          <Typography
            component="span"
            data-testid="commercial-status-unseen"
            sx={{ fontSize: '10px', lineHeight: '12px', fontWeight: 600, color: '#D4D4D4' }}
          >
            {unseen > 99 ? '99+' : unseen}
          </Typography>
        )}
        {mismatch && (
          <ReportProblemOutlinedIcon data-testid="commercial-status-mismatch" sx={{ fontSize: 12, color: MUTED }} />
        )}
      </Box>
    </Tooltip>
  );
}
